import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Plus, Trash2, Loader, Plane, MapPin, Moon, Check, Pencil, FileText, ChevronDown, WifiOff } from 'lucide-react';
import { tripService, clientService } from '../services/api';
import { useDialog } from '../context/DialogContext';
import useOnlineStatus from '../hooks/useOnlineStatus';
import MobilePageHeader from '../components/mobile/MobilePageHeader';
import SearchPicker from '../components/mobile/SearchPicker';
import NewLocationSheet from '../components/mobile/NewLocationSheet';
import PdfReadySheet from '../components/mobile/PdfReadySheet';
import { rebuildDays, unansweredNights as countUnanswered, buildTripPayload } from '../utils/tripWizard';

const STEPS = ['Basics', 'Destinations', 'Days & nights', 'Review'];

const COST_FIELDS = [
  { key: 'bus_amount', label: 'Public transport (€)' },
  { key: 'taxi_amount', label: 'Taxi / Uber (€)' },
  { key: 'nkso1_amount', label: 'Other expenses 1 (€)' },
  { key: 'nkso2_amount', label: 'Other expenses 2 (€)' },
  { key: 'park_amount', label: 'Parking (€)' },
  { key: 'bewk_amount', label: 'Entertainment (€)' },
  { key: 'km_amount', label: 'Private car (km)', step: '1' },
];
const COST_KEYS = { bus_amount: 'bus', taxi_amount: 'taxi', nkso1_amount: 'nkso1', nkso2_amount: 'nkso2', park_amount: 'park', bewk_amount: 'bewk', km_amount: 'km' };

const inputClass = 'w-full rounded-xl border border-gray-300 px-3 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white';

const fmtDay = (date) => format(parseISO(date), 'EEE d MMM', { locale: enUS });

function Segmented({ value, options, onChange }) {
  return (
    <div className="grid rounded-xl bg-gray-100 p-1" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map(o => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          className={`py-2 rounded-lg text-sm font-medium ${
            value === o.value ? 'bg-white shadow text-gray-900' : 'text-gray-500'
          }`}
          aria-pressed={value === o.value}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ToggleChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium border ${
        active ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-gray-300 text-gray-700'
      }`}
    >
      {active && <Check className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

/**
 * Mobile trip creation/editing as a 4-step wizard. The last step saves the
 * trip and then offers cost overrides and the travel allowance PDF straight
 * away (no save → reopen round trip).
 */
function TripWizard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { toast, confirm } = useDialog();
  const online = useOnlineStatus();

  const [loading, setLoading] = useState(isEdit);
  const [step, setStep] = useState(isEdit ? 3 : 0);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(id || null);
  const [dirty, setDirty] = useState(!isEdit);
  const [clients, setClients] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  const [form, setForm] = useState({
    name: '',
    start: '',
    end: '',
    is_foreign_travel: false,
    border_crossing_outbound: '',
    border_crossing_return: '',
    seminar_includes_meals: null,
    notes: '',
    days: [],
    destinations: [],
  });

  // Inline facility creation: resolves the picker's pending onCreate promise
  const [newLocation, setNewLocation] = useState(null); // { destIdx, clientId, label }
  const newLocationResolver = useRef(null);

  // Allowance PDF
  const [costOverrides, setCostOverrides] = useState(Object.fromEntries(COST_FIELDS.map(f => [f.key, ''])));
  const [showCosts, setShowCosts] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdf, setPdf] = useState(null);

  const loadCosts = (tripId) =>
    tripService.getComputedCosts(tripId)
      .then(costs => setCostOverrides(Object.fromEntries(
        COST_FIELDS.map(f => [f.key, costs[COST_KEYS[f.key]] != null ? String(costs[COST_KEYS[f.key]]) : ''])
      )))
      .catch(err => console.log('Could not load computed costs:', err));

  useEffect(() => {
    clientService.getClients().then(setClients).catch(console.error);
    if (!isEdit) return;
    tripService.getTrip(id)
      .then(trip => {
        const days = trip.days.map(d => ({
          date: format(parseISO(d.date), 'yyyy-MM-dd'),
          departure_time: d.departure_time || '',
          end_time: d.end_time || '',
          breakfast_covered: d.breakfast_covered,
          lunch_covered: d.lunch_covered,
          dinner_covered: d.dinner_covered,
          overnight: d.overnight,
        }));
        setForm({
          name: trip.name,
          start: days[0]?.date || '',
          end: days[days.length - 1]?.date || '',
          is_foreign_travel: trip.is_foreign_travel,
          border_crossing_outbound: trip.border_crossing_outbound
            ? format(parseISO(trip.border_crossing_outbound), "yyyy-MM-dd'T'HH:mm") : '',
          border_crossing_return: trip.border_crossing_return
            ? format(parseISO(trip.border_crossing_return), "yyyy-MM-dd'T'HH:mm") : '',
          seminar_includes_meals: trip.seminar_includes_meals,
          notes: trip.notes || '',
          days,
          destinations: trip.destinations.map(d => ({
            client_id: String(d.client_id),
            location_id: d.location_id ? String(d.location_id) : '',
          })),
        });
      })
      .catch(() => {
        toast('Error loading trip', 'error');
        navigate('/trips');
      })
      .finally(() => setLoading(false));
    loadCosts(id);
  }, [id, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (changes) => {
    setDirty(true);
    setForm(f => ({ ...f, ...changes }));
  };

  // Rebuild the day list when the dates change, keeping answers for kept dates
  const setDates = (start, end) => {
    setDirty(true);
    setForm(f => ({ ...f, start, end, days: rebuildDays(f.days, start, end) }));
  };

  const setDay = (idx, changes) => {
    setDirty(true);
    setForm(f => {
      const days = [...f.days];
      days[idx] = { ...days[idx], ...changes };
      return { ...f, days };
    });
  };

  const setAllNights = (value) => {
    setDirty(true);
    setForm(f => ({
      ...f,
      days: f.days.map((d, i) => (i < f.days.length - 1 ? { ...d, overnight: value } : d)),
    }));
  };

  const setDest = (idx, changes) => {
    setDirty(true);
    setForm(f => {
      const destinations = [...f.destinations];
      destinations[idx] = { ...destinations[idx], ...changes };
      return { ...f, destinations };
    });
  };

  const createClient = async (name) => {
    try {
      const client = await clientService.createClient({ name });
      setClients(list => [...list, { ...client, locations: client.locations || [] }]);
      return client.id;
    } catch (err) {
      toast('Could not create client', 'error');
      return null;
    }
  };

  // Called by the location picker: opens the facility sheet, resolves with the new id
  const requestNewLocation = (destIdx, clientId, label) => new Promise((resolve) => {
    newLocationResolver.current = resolve;
    setNewLocation({ destIdx, clientId, label });
  });

  const finishNewLocation = async (fields) => {
    if (!fields) {
      newLocationResolver.current?.(null);
      newLocationResolver.current = null;
      setNewLocation(null);
      return;
    }
    try {
      const loc = await clientService.addLocation(newLocation.clientId, fields);
      setClients(list => list.map(c =>
        String(c.id) === String(newLocation.clientId) ? { ...c, locations: [...c.locations, loc] } : c
      ));
      newLocationResolver.current?.(loc.id);
      newLocationResolver.current = null;
      setNewLocation(null);
    } catch (err) {
      toast('Could not create facility', 'error');
    }
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const nights = form.days.slice(0, -1);
  const unansweredNights = countUnanswered(form.days);
  const stepErrors = useMemo(() => [
    [
      !form.name.trim() && 'Give the trip a name',
      (!form.start || !form.end) && 'Choose the start and end dates',
      form.start && form.end && form.end < form.start && 'The end date is before the start date',
    ].filter(Boolean),
    form.destinations.some(d => !d.client_id) ? ['Pick a client for each destination, or remove it'] : [],
    unansweredNights > 0 ? [`Answer the overnight question for ${unansweredNights} night${unansweredNights > 1 ? 's' : ''}`] : [],
    [],
  ], [form, unansweredNights]);

  const firstInvalidStep = stepErrors.findIndex(e => e.length > 0);

  const goNext = () => {
    if (stepErrors[step].length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    setShowErrors(false);
    if (step > 0 && !(isEdit && step === 3)) setStep(s => s - 1);
    else if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/trips');
  };

  // ── Save / delete / PDF ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (firstInvalidStep !== -1) {
      setStep(firstInvalidStep);
      setShowErrors(true);
      return;
    }
    setSaving(true);
    try {
      const payload = buildTripPayload(form);
      const trip = savedId
        ? await tripService.updateTrip(savedId, payload)
        : await tripService.createTrip(payload);
      setSavedId(String(trip.id));
      setDirty(false);
      toast(savedId ? 'Trip updated' : 'Trip created');
      loadCosts(trip.id);
    } catch (err) {
      toast(online ? 'Error saving trip' : "You're offline — trips need a connection", 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!await confirm({
      title: 'Delete this trip?',
      message: 'This cannot be undone. Linked expenses will not be deleted.',
      confirmLabel: 'Delete',
      destructive: true,
    })) return;
    try {
      await tripService.deleteTrip(savedId);
      toast('Trip deleted');
      navigate('/trips');
    } catch (err) {
      toast('Error deleting trip', 'error');
    }
  };

  const handleGeneratePdf = async () => {
    setPdfLoading(true);
    try {
      const overrides = {};
      Object.entries(costOverrides).forEach(([k, v]) => {
        if (v !== '') overrides[k] = parseFloat(v);
      });
      const blob = await tripService.generatePdf(savedId, overrides);
      const safeName = form.name.trim().replace(/[^\w\- ]+/g, '').replace(/\s+/g, '_') || 'trip';
      setPdf({ blob, filename: `Travel_allowance_${safeName}.pdf` });
    } catch (err) {
      toast('Error generating PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const clientOptions = clients.map(c => ({
    id: c.id,
    label: c.name,
    sublabel: `${c.locations.length} facilit${c.locations.length === 1 ? 'y' : 'ies'}`,
  }));
  const locationOptions = (clientId) => {
    const client = clients.find(c => String(c.id) === String(clientId));
    return (client?.locations || []).map(loc => ({
      id: loc.id,
      label: loc.label || loc.city || 'Unnamed',
      sublabel: [loc.city, loc.country].filter(Boolean).join(', '),
    }));
  };
  const clientName = (clientId) => clients.find(c => String(c.id) === String(clientId))?.name;
  const errors = showErrors ? stepErrors[step] : [];

  return (
    <div className="pb-28">
      <MobilePageHeader
        title={isEdit ? form.name || 'Edit trip' : 'New trip'}
        subtitle={`Step ${step + 1} of ${STEPS.length} · ${STEPS[step]}`}
        onBack={goBack}
      />

      {/* Step indicator */}
      <div className="flex gap-1.5 mb-5">
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => (i <= step || isEdit || savedId) && setStep(i)}
            className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-blue-600' : 'bg-gray-200'}`}
            aria-label={`Step ${i + 1}: ${label}`}
          />
        ))}
      </div>

      {!online && (
        <div className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          You're offline. You can fill in the trip, but saving needs a connection.
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 space-y-0.5">
          {errors.map(e => <p key={e}>{e}</p>)}
        </div>
      )}

      {/* ── Step 1: Basics ── */}
      {step === 0 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip reason / name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => update({ name: e.target.value })}
              placeholder="e.g. Visit Enfinium plants in UK"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First day</label>
              <input
                type="date"
                value={form.start}
                onChange={e => setDates(e.target.value, form.end && form.end >= e.target.value ? form.end : e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last day</label>
              <input
                type="date"
                value={form.end}
                min={form.start || undefined}
                onChange={e => setDates(form.start || e.target.value, e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          {form.days.length > 0 && (
            <p className="-mt-3 text-sm text-gray-500">
              {form.days.length} day{form.days.length > 1 ? 's' : ''}, {nights.length} night{nights.length !== 1 ? 's' : ''}
            </p>
          )}

          <div className="rounded-2xl bg-white border border-gray-200 p-4 space-y-3">
            <label className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 font-medium text-gray-900">
                <Plane className="w-5 h-5 text-blue-500" />
                Foreign travel (outside Germany)
              </span>
              <input
                type="checkbox"
                checked={form.is_foreign_travel}
                onChange={e => update({ is_foreign_travel: e.target.checked })}
                className="w-6 h-6 rounded text-blue-600"
              />
            </label>
            {form.is_foreign_travel && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Left Germany (Grenzübertritt Hinreise)</label>
                  <input
                    type="datetime-local"
                    value={form.border_crossing_outbound}
                    onChange={e => update({ border_crossing_outbound: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Back in Germany (Grenzübertritt Rückreise)</label>
                  <input
                    type="datetime-local"
                    value={form.border_crossing_return}
                    onChange={e => update({ border_crossing_return: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Seminar/conference — registration includes free meals?</p>
            <Segmented
              value={form.seminar_includes_meals}
              onChange={v => update({ seminar_includes_meals: v })}
              options={[{ value: null, label: 'N/A' }, { value: true, label: 'Yes' }, { value: false, label: 'No' }]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => update({ notes: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* ── Step 2: Destinations ── */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Where are you going? Type a new name in the picker to create a client or facility.
          </p>
          {form.destinations.map((dest, idx) => (
            <div key={idx} className="rounded-2xl bg-white border border-gray-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Destination {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => update({ destinations: form.destinations.filter((_, i) => i !== idx) })}
                  className="p-2 -mr-1 rounded-full text-gray-400 active:bg-gray-100"
                  aria-label="Remove destination"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              <SearchPicker
                label="Client"
                value={dest.client_id}
                options={clientOptions}
                placeholder="Choose a client"
                createLabel="Create client"
                onSelect={clientId => setDest(idx, { client_id: String(clientId), location_id: '' })}
                onCreate={createClient}
              />
              <SearchPicker
                label="Facility"
                value={dest.location_id}
                options={locationOptions(dest.client_id)}
                noneLabel="Any facility"
                placeholder="Any facility"
                disabled={!dest.client_id}
                createLabel="Create facility"
                onSelect={locId => setDest(idx, { location_id: locId === '' ? '' : String(locId) })}
                onCreate={label => requestNewLocation(idx, dest.client_id, label)}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => update({ destinations: [...form.destinations, { client_id: '', location_id: '' }] })}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-300 text-blue-600 font-medium active:bg-gray-50"
          >
            <Plus className="w-5 h-5" />
            Add destination
          </button>
        </div>
      )}

      {/* ── Step 3: Days & nights ── */}
      {step === 2 && (
        <div className="space-y-4">
          {nights.length > 0 && (
            <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4">
              <p className="text-sm text-indigo-900">
                <strong>Overnight allowance:</strong> answer <em>Yes</em> only for nights where <strong>you</strong> paid
                for lodging (not the company). Prints “J”; <em>No</em> prints “N”.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setAllNights(true)} className="py-2.5 rounded-xl bg-white border border-indigo-200 text-sm font-medium text-indigo-700 active:bg-indigo-100">
                  All nights: Yes
                </button>
                <button type="button" onClick={() => setAllNights(false)} className="py-2.5 rounded-xl bg-white border border-indigo-200 text-sm font-medium text-indigo-700 active:bg-indigo-100">
                  All nights: No
                </button>
              </div>
            </div>
          )}

          {form.days.length === 0 && (
            <p className="text-sm text-gray-500">Set the trip dates in step 1 first.</p>
          )}

          {form.days.map((day, idx) => {
            const isLast = idx === form.days.length - 1;
            const unanswered = !isLast && day.overnight === null;
            return (
              <div
                key={day.date}
                className={`rounded-2xl bg-white border p-4 space-y-3 ${showErrors && unanswered ? 'border-red-300' : 'border-gray-200'}`}
              >
                <div className="flex items-baseline justify-between">
                  <p className="font-semibold text-gray-900">{fmtDay(day.date)}</p>
                  <p className="text-xs text-gray-400">Day {idx + 1}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{idx === 0 ? 'Departure' : 'Start'}</label>
                    <input
                      type="time"
                      value={day.departure_time}
                      onChange={e => setDay(idx, { departure_time: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{isLast ? 'Return' : 'End'}</label>
                    <input
                      type="time"
                      value={day.end_time}
                      onChange={e => setDay(idx, { end_time: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Meals provided (free)</p>
                  <div className="flex flex-wrap gap-2">
                    <ToggleChip active={day.breakfast_covered} onClick={() => setDay(idx, { breakfast_covered: !day.breakfast_covered })}>Breakfast</ToggleChip>
                    <ToggleChip active={day.lunch_covered} onClick={() => setDay(idx, { lunch_covered: !day.lunch_covered })}>Lunch</ToggleChip>
                    <ToggleChip active={day.dinner_covered} onClick={() => setDay(idx, { dinner_covered: !day.dinner_covered })}>Dinner</ToggleChip>
                  </div>
                </div>

                {!isLast && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-800 mb-2 flex items-center gap-1.5">
                      <Moon className="w-4 h-4 text-indigo-500" />
                      Night to {fmtDay(form.days[idx + 1].date)}: lodging paid by you?
                    </p>
                    <Segmented
                      value={day.overnight}
                      onChange={v => setDay(idx, { overnight: v })}
                      options={[{ value: true, label: 'Yes (J)' }, { value: false, label: 'No (N)' }]}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Step 4: Review ── */}
      {step === 3 && (
        <div className="space-y-4">
          <ReviewCard title="Basics" onEdit={() => setStep(0)}>
            <p className="font-medium text-gray-900">{form.name || '—'}</p>
            <p className="text-sm text-gray-600">
              {form.start ? `${fmtDay(form.start)} – ${fmtDay(form.end)}` : 'No dates'}
              {form.is_foreign_travel && ' · Foreign travel'}
            </p>
          </ReviewCard>

          <ReviewCard title="Destinations" onEdit={() => setStep(1)}>
            {form.destinations.filter(d => d.client_id).length === 0 ? (
              <p className="text-sm text-gray-500">None</p>
            ) : form.destinations.filter(d => d.client_id).map((d, i) => {
              const loc = locationOptions(d.client_id).find(l => String(l.id) === String(d.location_id));
              return (
                <p key={i} className="text-sm text-gray-800 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  {clientName(d.client_id)}{loc && <span className="text-gray-500"> · {loc.label}</span>}
                </p>
              );
            })}
          </ReviewCard>

          <ReviewCard title="Nights" onEdit={() => setStep(2)}>
            {nights.length === 0 ? (
              <p className="text-sm text-gray-500">Day trip — no nights</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {nights.map(d => (
                  <span
                    key={d.date}
                    className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      d.overnight === null ? 'bg-red-100 text-red-700'
                        : d.overnight ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {format(parseISO(d.date), 'd MMM')}: {d.overnight === null ? '?' : d.overnight ? 'J' : 'N'}
                  </span>
                ))}
              </div>
            )}
          </ReviewCard>

          {savedId && !dirty && (
            <div className="rounded-2xl bg-white border border-gray-200 p-4 space-y-3">
              <p className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Travel allowance PDF
              </p>
              <button
                type="button"
                onClick={() => setShowCosts(v => !v)}
                className="w-full flex items-center justify-between text-sm text-blue-600"
                aria-expanded={showCosts}
              >
                Costs from linked expenses (edit to override)
                <ChevronDown className={`w-4 h-4 transition-transform ${showCosts ? 'rotate-180' : ''}`} />
              </button>
              {showCosts && (
                <div className="grid grid-cols-2 gap-3">
                  {COST_FIELDS.map(({ key, label, step: inputStep }) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step={inputStep || '0.01'}
                        min="0"
                        value={costOverrides[key]}
                        onChange={e => setCostOverrides(o => ({ ...o, [key]: e.target.value }))}
                        placeholder="auto"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base"
                      />
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={pdfLoading}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 text-white font-medium active:bg-indigo-700 disabled:opacity-60"
              >
                {pdfLoading && <Loader className="w-5 h-5 animate-spin" />}
                {pdfLoading ? 'Generating…' : 'Generate allowance PDF'}
              </button>
            </div>
          )}

          {savedId && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-red-600 font-medium active:bg-red-50"
            >
              <Trash2 className="w-5 h-5" />
              Delete trip
            </button>
          )}
        </div>
      )}

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-3 pb-safe">
        {step < 3 ? (
          <button
            type="button"
            onClick={goNext}
            className="w-full py-3.5 rounded-xl bg-blue-600 text-white text-base font-semibold active:bg-blue-700"
          >
            {step === 2 && unansweredNights > 0 ? `Answer ${unansweredNights} more night${unansweredNights > 1 ? 's' : ''}` : 'Next'}
          </button>
        ) : savedId && !dirty ? (
          <button
            type="button"
            onClick={() => navigate('/trips')}
            className="w-full py-3.5 rounded-xl border border-gray-300 text-gray-800 text-base font-semibold active:bg-gray-50"
          >
            Done
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 text-white text-base font-semibold active:bg-blue-700 disabled:opacity-60"
          >
            {saving && <Loader className="w-5 h-5 animate-spin" />}
            {savedId ? 'Save changes' : 'Create trip'}
          </button>
        )}
      </div>

      <NewLocationSheet
        open={Boolean(newLocation)}
        clientName={newLocation && clientName(newLocation.clientId)}
        initialLabel={newLocation?.label || ''}
        onCancel={() => finishNewLocation(null)}
        onSave={finishNewLocation}
      />

      <PdfReadySheet pdf={pdf} onClose={() => setPdf(null)} title="Allowance PDF ready" />
    </div>
  );
}

function ReviewCard({ title, onEdit, children }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
        <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-sm text-blue-600 -my-1 py-1">
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export default TripWizard;
